import type { RecordOrigin } from '@lio/api-types';

export type OriginLine = { label: string; value: string };

// @lio/api-types is types-only, so the enum's runtime values aren't importable.
// Keys are the RecordOrigin string values; the Record type enforces full coverage.
const RECORD_ORIGIN_LABELS: Record<RecordOrigin, string> = {
  LioPortalMcp: 'LIO Portal MCP',
  PortalUi: 'Portal UI',
};

const PORTAL_UI: RecordOrigin = 'PortalUi';

// Null (no stamp — pre-existing or system-written) and PortalUi are both the
// standard human path and render nothing.
const isDefaultOrigin = (origin?: RecordOrigin | null): boolean => !origin || origin === PORTAL_UI;

const labelFor = (origin: RecordOrigin): string => RECORD_ORIGIN_LABELS[origin];

/** Single "Origin" line for records with only a creation origin (Notes, Activity). */
export const getOriginLine = (origin?: RecordOrigin | null): OriginLine | null =>
  isDefaultOrigin(origin) ? null : { label: 'Origin', value: labelFor(origin as RecordOrigin) };

/**
 * Origin lines for a referral, comparing the creation origin against the
 * resolution origin. Nothing when both are the standard path; a single line
 * when unresolved or when both channels match and are non-default; two lines
 * when a resolved referral was created and resolved through different channels.
 */
export const getReferralOriginLines = (
  origin: RecordOrigin | null | undefined,
  resolvedOrigin: RecordOrigin | null | undefined,
  isResolved: boolean,
): OriginLine[] => {
  const normalize = (o?: RecordOrigin | null): RecordOrigin => o ?? PORTAL_UI;

  if (!isResolved || normalize(origin) === normalize(resolvedOrigin)) {
    return isDefaultOrigin(origin) ? [] : [{ label: 'Origin', value: labelFor(origin as RecordOrigin) }];
  }

  return [
    { label: 'Origin', value: labelFor(normalize(origin)) },
    { label: 'Resolved Origin', value: labelFor(normalize(resolvedOrigin)) },
  ];
};
